import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import { useLocation } from '../../contexts/LocationContext';
import { supabaseClient } from '../../services/supabase';
import { commentSafetyService, CommentSafetyCheck } from '../../services/commentSafetyService';
import './ChartComments.css';

interface Comment {
  id: string;
  content: string;
  user_id: string;
  user_email?: string;
  user_location?: string;
  user_type?: string;
  user_first_name?: string;
  created_at: string;
  post_type: string;
  chart_context?: string;
  location_context?: string;
  parent_id?: string;
  user_profile?: {
    full_name: string;
    email: string;
    role: string;
    user_metadata?: {
      organization?: string;
      location_type?: string;
      specific_location?: string;
    };
  };
}

interface ChartCommentsProps {
  chartName: string;
  location?: string;
  className?: string;
  visible?: boolean;
  showToggle?: boolean;
}

export const ChartComments: React.FC<ChartCommentsProps> = ({ 
  chartName, 
  location,
  className = '',
  visible = false,
  showToggle = true
}) => {
  const { user } = useAuth();
  const { userProfile } = useUserRole();
  const { selectedLocation } = useLocation();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showComments, setShowComments] = useState(visible);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commentsCount, setCommentsCount] = useState(0);
  const [safetyCheck, setSafetyCheck] = useState<CommentSafetyCheck | null>(null);
  const [showCAPTCHA, setShowCAPTCHA] = useState(false);
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deletingComment, setDeletingComment] = useState<string | null>(null);
  const [replyingToComment, setReplyingToComment] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replies, setReplies] = useState<Record<string, Comment[]>>({});

  const fetchCommentsCount = useCallback(async () => {
    try {
      const { count, error } = await supabaseClient
        .from('community_comments')
        .select('*', { count: 'exact', head: true })
        .eq('chart_context', chartName)
        .is('parent_id', null);

      if (error) {
        console.error('Error fetching comments count:', error);
        return;
      }

      setCommentsCount(count || 0);
    } catch (err) {
      console.error('Error fetching comments count:', err);
    }
  }, [chartName]);

  const fetchReplies = useCallback(async (commentId: string) => {
    try {
      const { data, error } = await supabaseClient
        .from('community_comments')
        .select('*')
        .eq('parent_id', commentId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching replies:', error);
        return [];
      }

      // Fetch user profiles for each reply
      const repliesWithProfiles = await Promise.all(
        (data || []).map(async (reply) => {
          const { data: profile } = await supabaseClient
            .from('user_profiles')
            .select('full_name, email, role, user_metadata')
            .eq('id', reply.user_id)
            .single();
          
          return {
            ...reply,
            user_profile: profile
          };
        })
      );

      return repliesWithProfiles;
    } catch (err) {
      console.error('Error fetching replies:', err);
      return [];
    }
  }, []);

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabaseClient
        .from('community_comments')
        .select('*')
        .eq('chart_context', chartName)
        .is('parent_id', null) // Only top-level comments
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching chart comments:', error);
        // If table doesn't exist, show a helpful message
        if (error.message.includes('relation "community_comments" does not exist')) {
          setError('Comments feature not yet set up. Please run the database setup script.');
        } else {
          setError('Failed to load comments');
        }
        return;
      }

      // Fetch user profiles for each comment
      const commentsWithProfiles = await Promise.all(
        (data || []).map(async (comment) => {
          const { data: profile } = await supabaseClient
            .from('user_profiles')
            .select('full_name, email, role, user_metadata')
            .eq('id', comment.user_id)
            .single();
          
          return {
            ...comment,
            user_profile: profile
          };
        })
      );

      // Sort comments: float comments from user's selected location to the top
      const userLocation = selectedLocation || location || '';
      const sortedComments = commentsWithProfiles.sort((a, b) => {
        const aMatchesLocation = getUserLocation(a).includes(userLocation);
        const bMatchesLocation = getUserLocation(b).includes(userLocation);
        
        if (aMatchesLocation && !bMatchesLocation) return -1;
        if (!aMatchesLocation && bMatchesLocation) return 1;
        return 0; // Keep original order for same location group
      });
      
      setComments(sortedComments);

      // Fetch replies for all comments
      const repliesMap: Record<string, Comment[]> = {};
      for (const comment of sortedComments) {
        repliesMap[comment.id] = await fetchReplies(comment.id);
      }
      setReplies(repliesMap);
    } catch (err) {
      console.error('Error fetching chart comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [chartName, selectedLocation, location]);

  // Generate simple CAPTCHA question
  const generateCAPTCHA = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const answer = num1 + num2;
    setCaptchaQuestion(`What is ${num1} + ${num2}?`);
    return answer.toString();
  };

  // Check comment safety before submission
  const checkCommentSafety = async (content: string): Promise<CommentSafetyCheck> => {
    if (!user) {
      return { isAllowed: false, reason: 'Please sign in to comment' };
    }

    const safetyResult = await commentSafetyService.checkCommentSafety(
      user.id,
      content,
      userProfile?.role
    );

    setSafetyCheck(safetyResult);

    if (safetyResult.requiresCAPTCHA) {
      setShowCAPTCHA(true);
      generateCAPTCHA();
    }

    return safetyResult;
  };

  // Load comments count when component mounts
  useEffect(() => {
    fetchCommentsCount();
  }, [fetchCommentsCount]);

  useEffect(() => {
    if (showComments) {
      console.log('ChartComments: Fetching comments for chart:', chartName);
      fetchComments();
    }
  }, [showComments, fetchComments, chartName]);

  const handleSubmitComment = async () => {
    if (!user || !newComment.trim()) return;

    try {
      setSubmitting(true);
      setError(null);
      setSafetyCheck(null);

      // Check comment safety first
      const safetyResult = await checkCommentSafety(newComment.trim());
      
      if (!safetyResult.isAllowed) {
        setError(safetyResult.reason || 'Comment not allowed');
        if (safetyResult.cooldownTime) {
          setTimeout(() => {
            setError(null);
            setSafetyCheck(null);
          }, safetyResult.cooldownTime);
        }
        return;
      }

      // If CAPTCHA is required, verify it
      if (safetyResult.requiresCAPTCHA) {
        if (!showCAPTCHA) {
          // First time CAPTCHA is required - show it
          setShowCAPTCHA(true);
          generateCAPTCHA();
          setError('Please complete CAPTCHA verification');
          return;
        }
        
        // CAPTCHA is shown, verify the answer
        const expectedAnswer = captchaQuestion.split(' ')[2].replace('?', '');
        const userAnswer = captchaValue.trim();
        
        if (userAnswer !== expectedAnswer) {
          setError('CAPTCHA verification failed. Please try again.');
          setCaptchaValue('');
          generateCAPTCHA();
          return;
        }
      }

      console.log('ChartComments: Submitting comment with location:', location);
      console.log('ChartComments: User ID:', user.id);
      console.log('ChartComments: User Profile:', userProfile);

      // Get user's first name and location from profile
      const firstName = userProfile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';
      
      // Use user's profile location if available, otherwise fallback to chart location
      const userProfileLocation = user?.user_metadata?.specific_location && user?.user_metadata?.location_type 
        ? `${user.user_metadata.specific_location}, ${user.user_metadata.location_type}`
        : null;
      const userLocation = userProfileLocation || location || 'Unknown Location';

      const { error } = await supabaseClient
        .from('community_comments')
        .insert({
          content: newComment.trim(),
          post_type: 'comment',
          chart_context: chartName,
          location_context: userLocation,
          user_id: user.id,
          user_email: user.email,
          user_location: userLocation,
          user_type: userProfile?.role || 'user',
          user_first_name: firstName
        });

      if (error) {
        console.error('Error creating comment:', error);
        setError('Failed to create comment');
        return;
      }

      console.log('ChartComments: Comment saved successfully');

      // Record successful comment for safety tracking
      commentSafetyService.recordComment(user.id);

      // Reset form and refresh comments
      setNewComment('');
      setShowCAPTCHA(false);
      setCaptchaValue('');
      setCaptchaQuestion('');
      setSafetyCheck(null);
      await fetchComments();
      await fetchCommentsCount(); // Update the count
    } catch (err) {
      console.error('Error creating comment:', err);
      setError('Failed to create comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!user || !editContent.trim()) return;

    try {
      setError(null);
      setSafetyCheck(null);

      // Check comment safety first
      const safetyResult = await checkCommentSafety(editContent.trim());
      
      if (!safetyResult.isAllowed) {
        setError(safetyResult.reason || 'Comment not allowed');
        if (safetyResult.cooldownTime) {
          setTimeout(() => {
            setError(null);
            setSafetyCheck(null);
          }, safetyResult.cooldownTime);
        }
        return;
      }

      // If CAPTCHA is required, verify it
      if (safetyResult.requiresCAPTCHA) {
        if (!showCAPTCHA) {
          setShowCAPTCHA(true);
          generateCAPTCHA();
          setError('Please complete CAPTCHA verification');
          return;
        }
        
        const expectedAnswer = captchaQuestion.split(' ')[2].replace('?', '');
        const userAnswer = captchaValue.trim();
        
        if (userAnswer !== expectedAnswer) {
          setError('CAPTCHA verification failed. Please try again.');
          setCaptchaValue('');
          generateCAPTCHA();
          return;
        }
      }

      const { error } = await supabaseClient
        .from('community_comments')
        .update({ 
          content: editContent.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', commentId)
        .eq('user_id', user.id); // Ensure user can only edit their own comments

      if (error) {
        console.error('Error updating comment:', error);
        setError('Failed to update comment');
        return;
      }

      // Record successful comment for safety tracking
      commentSafetyService.recordComment(user.id);

      // Reset form and refresh comments
      setEditingComment(null);
      setEditContent('');
      setShowCAPTCHA(false);
      setCaptchaValue('');
      setCaptchaQuestion('');
      setSafetyCheck(null);
      await fetchComments();
      await fetchCommentsCount(); // Update the count
    } catch (err) {
      console.error('Error updating comment:', err);
      setError('Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!user) return;

    try {
      setError(null);

      const { error } = await supabaseClient
        .from('community_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id); // Ensure user can only delete their own comments

      if (error) {
        console.error('Error deleting comment:', error);
        setError('Failed to delete comment');
        return;
      }

      // Reset form and refresh comments
      setDeletingComment(null);
      await fetchComments();
      await fetchCommentsCount(); // Update the count
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Failed to delete comment');
    }
  };

  const startEdit = (comment: Comment) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
    setShowCAPTCHA(false);
    setCaptchaValue('');
    setCaptchaQuestion('');
    setSafetyCheck(null);
  };

  const cancelEdit = () => {
    setEditingComment(null);
    setEditContent('');
    setShowCAPTCHA(false);
    setCaptchaValue('');
    setCaptchaQuestion('');
    setSafetyCheck(null);
  };

  const handleSubmitReply = async (parentId: string) => {
    if (!user || !replyContent.trim()) return;

    try {
      setError(null);

      // Check comment safety
      const safetyResult = await checkCommentSafety(replyContent.trim());
      
      if (!safetyResult.isAllowed) {
        setError(safetyResult.reason || 'Reply not allowed');
        return;
      }

      const firstName = userProfile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';
      const userProfileLocation = user?.user_metadata?.specific_location && user?.user_metadata?.location_type 
        ? `${user.user_metadata.specific_location}, ${user.user_metadata.location_type}`
        : null;
      const userLocation = userProfileLocation || location || 'Unknown Location';

      const { error } = await supabaseClient
        .from('community_comments')
        .insert({
          content: replyContent.trim(),
          post_type: 'comment',
          chart_context: chartName,
          location_context: userLocation,
          parent_id: parentId,
          user_id: user.id,
          user_email: user.email,
          user_location: userLocation,
          user_type: userProfile?.role || 'user',
          user_first_name: firstName
        });

      if (error) {
        console.error('Error creating reply:', error);
        setError('Failed to create reply');
        return;
      }

      // Record successful reply for safety tracking
      commentSafetyService.recordComment(user.id);

      // Reset form and refresh comments
      setReplyContent('');
      setReplyingToComment(null);
      await fetchComments();
    } catch (err) {
      console.error('Error creating reply:', err);
      setError('Failed to create reply');
    }
  };

  const startReply = (commentId: string) => {
    setReplyingToComment(commentId);
    setReplyContent('');
  };

  const cancelReply = () => {
    setReplyingToComment(null);
    setReplyContent('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserDisplayName = (comment: Comment) => {
    // Try direct fields first, then fallback to profile
    return comment.user_first_name || 
           comment.user_profile?.full_name?.split(' ')[0] || 
           comment.user_email?.split('@')[0] || 
           comment.user_profile?.email?.split('@')[0] || 
           'Anonymous';
  };

  const getUserLocation = (comment: Comment) => {
    // Try direct fields first, then fallback to profile
    return comment.user_location || 
           comment.location_context ||
           (comment.user_profile?.user_metadata?.specific_location && comment.user_profile?.user_metadata?.location_type
             ? `${comment.user_profile.user_metadata.specific_location}, ${comment.user_profile.user_metadata.location_type}`
             : comment.user_profile?.user_metadata?.specific_location || 
           comment.user_profile?.user_metadata?.location_type || 
           'Location not specified');
  };

  const getUserType = (comment: Comment) => {
    // Try direct fields first, then fallback to profile
    return comment.user_type?.replace('_', ' ').toUpperCase() || 
           comment.user_profile?.role?.replace('_', ' ').toUpperCase() || 
           'USER';
  };

  // Update internal state when visible prop changes
  React.useEffect(() => {
    setShowComments(visible);
  }, [visible]);

  return (
    <div className={`chart-comments ${className}`}>
      {showToggle && (
        <button
          className="chart-comments-toggle"
          onClick={() => setShowComments(!showComments)}
        >
          💬 Comments ({commentsCount})
        </button>
      )}

      {showComments && (
        <div className="chart-comments-panel">
          <div className="chart-comments-header">
            <h4>Comments on {chartName}</h4>
            <button
              className="chart-comments-close"
              onClick={() => setShowComments(false)}
            >
              ×
            </button>
          </div>

          {error && (
            <div className="chart-comments-error">
              {error}
            </div>
          )}

          {/* Safety Warning */}
          {safetyCheck && !safetyCheck.isAllowed && (
            <div className="chart-comments-safety-warning">
              <div className="safety-warning-icon">⚠️</div>
              <div className="safety-warning-text">
                {safetyCheck.reason}
                {safetyCheck.cooldownTime && (
                  <div className="cooldown-timer">
                    Try again in {Math.ceil(safetyCheck.cooldownTime / 1000)} seconds
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CAPTCHA */}
          {showCAPTCHA && (
            <div className="chart-comments-captcha">
              <div className="captcha-question">{captchaQuestion}</div>
              <input
                type="text"
                value={captchaValue}
                onChange={(e) => setCaptchaValue(e.target.value)}
                placeholder="Your answer"
                className="captcha-input"
              />
              <div className="captcha-instruction">
                Enter your answer above, then click "Post Comment"
              </div>
            </div>
          )}

          {/* New Comment Form */}
          {user && (
            <div className="chart-comments-form">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Share your thoughts about this chart..."
                rows={3}
              />
              <div className="chart-comments-form-actions">
                <button
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim() || submitting}
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </button>
                <button
                  onClick={() => {
                    setNewComment('');
                    setShowComments(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Comments List */}
          <div className="chart-comments-list">
            {loading ? (
              <div className="chart-comments-loading">
                Loading comments...
              </div>
            ) : comments.length === 0 ? (
              <div className="chart-comments-empty">
                {user ? 'No comments yet. Be the first to comment!' : 'Sign in to view and add comments.'}
              </div>
            ) : (
              comments.map(comment => {
                const commentReplies = replies[comment.id] || [];
                const isFromSameLocation = getUserLocation(comment).includes(selectedLocation || location || '');
                
                return (
                  <div key={comment.id} className={`chart-comment ${isFromSameLocation ? 'same-location' : ''}`}>
                    <div className="chart-comment-header">
                      <span className="chart-comment-author">{getUserDisplayName(comment)}</span>
                      <span className={`chart-comment-location ${isFromSameLocation ? 'highlight' : ''}`}>
                        📍 {getUserLocation(comment)}
                      </span>
                      <span className="chart-comment-user-type">{getUserType(comment)}</span>
                      <span className="chart-comment-date">{formatDate(comment.created_at)}</span>
                    </div>
                    <div className="chart-comment-content">
                      {comment.content}
                    </div>
                    {user && (
                      <div className="chart-comment-actions">
                        {user.id === comment.user_id && (
                          <>
                            <button
                              className="chart-edit-button"
                              onClick={() => startEdit(comment)}
                            >
                              Edit
                            </button>
                            <button
                              className="chart-delete-button"
                              onClick={() => setDeletingComment(comment.id)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                        <button
                          className="chart-reply-button"
                          onClick={() => startReply(comment.id)}
                        >
                          Reply
                        </button>
                      </div>
                    )}
                    
                    {/* Replies List */}
                    {commentReplies.length > 0 && (
                      <div className="chart-replies-list">
                        {commentReplies.map(reply => (
                          <div key={reply.id} className="chart-reply">
                            <div className="chart-reply-header">
                              <span className="chart-reply-author">{getUserDisplayName(reply)}</span>
                              <span className="chart-reply-location">📍 {getUserLocation(reply)}</span>
                              <span className="chart-reply-date">{formatDate(reply.created_at)}</span>
                            </div>
                            <div className="chart-reply-content">
                              {reply.content}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply Form */}
                    {replyingToComment === comment.id && (
                      <div className="chart-reply-form">
                        <textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Write your reply..."
                          rows={2}
                        />
                        <div className="chart-reply-actions">
                          <button
                            onClick={() => handleSubmitReply(comment.id)}
                            disabled={!replyContent.trim()}
                          >
                            Post Reply
                          </button>
                          <button onClick={cancelReply}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Edit Form */}
          {editingComment && user && (
            <div className="chart-edit-form">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="Edit your comment..."
                rows={3}
              />
              {showCAPTCHA && (
                <div className="chart-comments-captcha">
                  <div className="captcha-question">{captchaQuestion}</div>
                  <input
                    type="text"
                    value={captchaValue}
                    onChange={(e) => setCaptchaValue(e.target.value)}
                    placeholder="Your answer"
                    className="captcha-input"
                  />
                  <div className="captcha-instruction">
                    Enter your answer above, then click 'Update Comment'
                  </div>
                </div>
              )}
              <div className="chart-form-actions">
                <button 
                  onClick={() => handleEditComment(editingComment)} 
                  disabled={!editContent.trim()}
                >
                  Update Comment
                </button>
                <button onClick={cancelEdit}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          {deletingComment && (
            <div className="chart-delete-confirmation">
              <p>Are you sure you want to delete this comment? This action cannot be undone.</p>
              <div className="chart-form-actions">
                <button 
                  className="chart-delete-confirm-button"
                  onClick={() => handleDeleteComment(deletingComment)}
                >
                  Yes, Delete
                </button>
                <button onClick={() => setDeletingComment(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!user && (
            <div className="chart-comments-login-prompt">
              <p>Please sign in to comment on charts.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
